import { AnonUserCriteria } from 'modules/graphql/types/GraphQLAnonymousCriteria';
import { FC, useState } from 'react';
import { useSelector } from 'react-redux';
import { BUTTON_SIZES, BUTTON_VARIANTS, IconButton } from 'ui/button';
import { LinkAddButton } from 'ui/linkButton';
import { PageHeader } from 'ui/pageHeader';
import { spacing } from 'ui/theme';
import { FlexSpacer } from 'ui/flexSpacer';
import { ActionMenu } from 'ui/actionMenu';
import { DatabaseAccept16, Overflow24 } from 'ui/icons';
import { AllowedActionMenuElements, MENU_ALIGNMENTS } from 'ui/actionMenu/types';
import { selectIsAnonymousUserRetentionFeatureFlagOn } from 'core/userContext/selectors';

import { AUA_CREATE_PAGE_PATH, CONTENT } from '../constants';
import { useCriteriaIndexQueryContext } from '../context/CriteriaIndexQueryContext';
import { CriteriaIndexQueryProvider } from '../context/CriteriaIndexQueryProvider';
import { AnonTrackingAnnouncements } from './Announcements/AnonUserTrackingAnnouncements';
import { AnonymousCriteriaFilterHeader } from './AnonymousCriteriaFilterHeader';
import { AnonymousCriteriaTable } from './AnonymousCriteriaTable';
import { CriteriaTableContainer } from './AnonymousUserTracking.styled';
import { CriteriaArchivalModal } from './Modals/CriteriaArchivalModal';
import { ToggleActiveModal } from './Modals/ToggleActiveModal';
import { CriteriaRetentionTakeover } from './CriteriaRetentionTakeover';
import { CriteriaRetentionContextProvider } from './CriteriaRetentionTakeover/CriteriaRetentionContextProvider';
import { useCriteriaRetentionContext } from './CriteriaRetentionTakeover/CriteriaRetentionContext';

const withContext = (Component: FC) => (props: any) => {
  return (
    <CriteriaIndexQueryProvider>
      <CriteriaRetentionContextProvider>
        <AnonTrackingAnnouncements />
        <Component {...props} />
      </CriteriaRetentionContextProvider>
    </CriteriaIndexQueryProvider>
  );
};

export const AnonCriteriaIndexPage = withContext(() => {
  const [isArchivalModalOpen, setIsArchivalModalOpen] = useState(false);
  const [isToggleActiveModalOpen, setIsToggleActiveModalOpen] = useState(false);
  const [actionModalCriteria, setActionModalCriteria] = useState<AnonUserCriteria | undefined>();
  const { setIsOpen } = useCriteriaRetentionContext();
  const { refetchCriterias } = useCriteriaIndexQueryContext();
  const isAnonymousUserRetentionFeatureFlagOn = useSelector(
    selectIsAnonymousUserRetentionFeatureFlagOn,
  );

  const pageHeaderMenuOptions = [
    {
      as: AllowedActionMenuElements.Button,
      content: (
        <>
          <DatabaseAccept16 isColorInherited={true} />
          <div>{CONTENT.criteriaIndexPage.criteriaRetentionTakeover}</div>
        </>
      ),
      onClick: () => {
        setIsOpen(true);
      },
      id: 'criteria-retention-takeover',
    },
  ];

  return (
    <>
      <PageHeader
        widthStyle={spacing.PageWidthStyle.fullWidth}
        pageActions={
          <FlexSpacer>
            {isAnonymousUserRetentionFeatureFlagOn && (
              <ActionMenu
                id="anon-criteria-index-page-overflow-menu"
                align={MENU_ALIGNMENTS.LEFT}
                options={pageHeaderMenuOptions}
              >
                <IconButton size={BUTTON_SIZES.LARGE} variant={BUTTON_VARIANTS.SECONDARY}>
                  <Overflow24 />
                </IconButton>
              </ActionMenu>
            )}
            <LinkAddButton
              data-test="anon-criteria-index-page-create-button"
              to={AUA_CREATE_PAGE_PATH}
              size={BUTTON_SIZES.LARGE}
            >
              {CONTENT.criteriaIndexPage.newCriteriaBtnLabel}
            </LinkAddButton>
          </FlexSpacer>
        }
      >
        {CONTENT.criteriaIndexPage.title}
      </PageHeader>
      <CriteriaTableContainer>
        <AnonymousCriteriaFilterHeader />
        <AnonymousCriteriaTable
          setIsArchivalModalOpen={setIsArchivalModalOpen}
          setIsToggleActiveModalOpen={setIsToggleActiveModalOpen}
          setActionModalCriteria={setActionModalCriteria}
        />
        {isArchivalModalOpen && actionModalCriteria && (
          <CriteriaArchivalModal
            archiving={!actionModalCriteria?.isArchived}
            onClose={() => setIsArchivalModalOpen(false)}
            criteria={actionModalCriteria}
            onSuccess={refetchCriterias}
          />
        )}
        {isToggleActiveModalOpen && actionModalCriteria && (
          <ToggleActiveModal
            activating={!actionModalCriteria?.fields?.isLive}
            onClose={() => setIsToggleActiveModalOpen(false)}
            criteria={actionModalCriteria}
            onSuccess={refetchCriterias}
          />
        )}
      </CriteriaTableContainer>
      <CriteriaRetentionTakeover />
    </>
  );
});
